import { Platform } from 'react-native';
import type { MapPin } from '@/components/maps/MapView';
import type { ItineraryItem } from '@/services/itinerary';
import type { Place } from '@/services/places';
import {
  addressLooksStructuredForGeocoding,
  buildItineraryGeocodeQueryText,
} from '@/utils/geocodeHelpers';

/**
 * Builds a search query for Apple/Google Maps. Matches in-app Mapbox geocoding (structured
 * street addresses skip venue/title `location` clutter from templates).
 */
export function buildExternalMapsSearchQuery(item: Pick<ItineraryItem, 'address' | 'location' | 'title'>): string {
  return buildItineraryGeocodeQueryText(item);
}

/** Single-line location for detail UI (address first, then city when different). */
export function formatItineraryLocationDisplay(item: Pick<ItineraryItem, 'address' | 'location'>): string {
  const address = (item.address || '').trim();
  const location = (item.location || '').trim();
  if (address && location && addressLooksStructuredForGeocoding(address)) {
    return address;
  }
  if (address && location && address !== location) {
    return `${address} · ${location}`;
  }
  return address || location;
}

const isUsableStoredCoord = (lat: number, lng: number): boolean =>
  !Number.isNaN(lat) &&
  !Number.isNaN(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180 &&
  !(lat === 0 && lng === 0) &&
  !(Math.abs(lat - 20) < 1 && Math.abs(lng) < 1);

/**
 * `https://maps.google.com/?q=...`
 * Prefer a **text search** from address/city when present so Apple/Google Maps can disambiguate
 * (e.g. "Rue Marrakech" in Berkane vs Marrakech). Stored coordinates are only used when there is
 * no usable address line (avoids sending users to a bad persisted pin).
 */
export function buildGoogleMapsSearchUrl(item: ItineraryItem): string | null {
  const query = buildExternalMapsSearchQuery(item).trim();
  if (query.length > 0) {
    return `https://maps.google.com/?q=${encodeURIComponent(query)}`;
  }
  const c = item.coordinates;
  if (
    c &&
    typeof c.latitude === 'number' &&
    typeof c.longitude === 'number' &&
    isUsableStoredCoord(c.latitude, c.longitude)
  ) {
    return `https://maps.google.com/?q=${encodeURIComponent(`${c.latitude},${c.longitude}`)}`;
  }
  return null;
}

function isItineraryItemData(data: unknown): data is ItineraryItem {
  return (
    !!data &&
    typeof data === 'object' &&
    'tripId' in data &&
    typeof (data as ItineraryItem).tripId === 'string' &&
    'startDate' in data
  );
}

/**
 * Human-readable destination for Apple/Google **directions** URLs.
 * Prefer address lines over pin coordinates so routing matches “View on map” / pasted addresses.
 */
export function buildDirectionsDestinationQuery(pin: MapPin): string | null {
  if (pin.type === 'itinerary' && pin.data && isItineraryItemData(pin.data)) {
    const q = buildExternalMapsSearchQuery(pin.data).trim();
    if (q.length > 0) return q;
  }
  const loc = (pin.locationText || '').trim();
  if (loc.length > 0) return loc;
  if (pin.type === 'landmark' && pin.data && typeof pin.data === 'object') {
    const place = pin.data as Partial<Place>;
    const bits = [place.name, place.address]
      .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
      .map((x) => x.trim());
    const joined = bits.join(', ');
    if (joined.length > 0) return joined;
  }
  return null;
}

export function buildExternalDirectionsUrl(
  pin: MapPin,
  mode: 'driving' | 'walking' | 'transit' = 'driving',
): string | null {
  const textDest = buildDirectionsDestinationQuery(pin);
  const appleMode = mode === 'walking' ? 'w' : mode === 'transit' ? 'r' : 'd';
  const googleMode = mode === 'walking' ? 'w' : mode === 'transit' ? 'r' : 'd';
  const webMode = mode === 'walking' ? 'walking' : mode === 'transit' ? 'transit' : 'driving';

  if (textDest && textDest.length > 0) {
    const enc = encodeURIComponent(textDest);
    return (
      Platform.select({
        ios: `maps://maps.apple.com/?daddr=${enc}&dirflg=${appleMode}`,
        android: `google.navigation:q=${enc}&mode=${googleMode}`,
        default: `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=${webMode}`,
      }) ?? null
    );
  }

  const { latitude, longitude } = pin.coordinates;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude === 0 && longitude === 0) return null;

  return (
    Platform.select({
      ios: `maps://maps.apple.com/?daddr=${latitude},${longitude}&dirflg=${appleMode}`,
      android: `google.navigation:q=${latitude},${longitude}&mode=${googleMode}`,
      default: `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=${webMode}`,
    }) ?? null
  );
}
